import { useState, useEffect, useRef } from 'react';
import { isMobile } from '@ringcentral/rwc-utils/src/client-detection';
const PORTRAIT = 'portrait';
const LANDSCAPE = 'landscape';

const useDetectKeyboardWithVirtualKeyboardApi = (): boolean => {
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    useEffect(() => {
        //https://developer.mozilla.org/en-US/docs/Web/API/VirtualKeyboard_API
        //@ts-expect-error VirtualKeyboard is only available in Chrome 94+
        const virtualKeyboard = navigator.virtualKeyboard;

        const handleGeometryChange = event => {
            const { height } = event.target.boundingRect;
            setIsKeyboardVisible(height > 0);
        };

        virtualKeyboard?.addEventListener('geometrychange', handleGeometryChange);

        return () => {
            virtualKeyboard?.removeEventListener('geometrychange', handleGeometryChange);
        };
    }, []);

    return isKeyboardVisible;
};

// when we use chrome mobile device view to do mobile browser support development
// if the dimension we set can not be shown without zooming the device view
// chrome may give us a wrong window.innerHeight while the orientation is changing
// which leads us to a wrong keyboard visibility calculation
// so we need this threshold to differentiate this case and the real mobile keyboard case
const MOBILE_KEYBOARD_HEIGHT_THRESHOLD = 200;

const getOrientation = () => (window.innerWidth > window.innerHeight ? LANDSCAPE : PORTRAIT);

const useFallbackDetectKeyboard = () => {
    const prevStateRef = useRef({
        width: window.innerWidth,
        height: window.innerHeight,
        orientation: getOrientation(),
        shouldResetHeight: false,
    });

    // this orientation is for deciding whether to reset the height
    // to get the actual orientation, use useOrientation instead
    // (these two may be different in some rare case)
    const [orientation, setOrientation] = useState(prevStateRef.current.orientation);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    useEffect(() => {
        if (!isMobile) return;

        const onResize = () => {
            const orientationChanged = prevStateRef.current.width !== window.innerWidth;
            if (orientationChanged) {
                setOrientation(getOrientation());
            } else {
                const keyboardVisible =
                    prevStateRef.current.height - window.innerHeight >
                    MOBILE_KEYBOARD_HEIGHT_THRESHOLD;
                setIsKeyboardVisible(keyboardVisible);
            }
        };

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!isMobile) return;

        const orientationChanged = prevStateRef.current.orientation !== orientation;

        if (orientationChanged) {
            prevStateRef.current.width = window.innerWidth;
            prevStateRef.current.height = window.innerHeight;
            prevStateRef.current.orientation = orientation;
            if (isKeyboardVisible) {
                // it covers the case when changing the orientation while the keyboard is showing
                prevStateRef.current.shouldResetHeight = true;
            }
        } else {
            if (!isKeyboardVisible && prevStateRef.current.shouldResetHeight) {
                prevStateRef.current.height = window.innerHeight;
                prevStateRef.current.shouldResetHeight = false;
            }
        }
    }, [orientation, isKeyboardVisible]);

    return isKeyboardVisible;
};

const useMobileKeyboard = (): boolean => {
    if (!('virtualKeyboard' in navigator)) {
        return useFallbackDetectKeyboard();
    }
    return useDetectKeyboardWithVirtualKeyboardApi();
};

export default useMobileKeyboard;
